/**
 * Root container for the "For You" tab content in the Rovo button mini-modal.
 */

import React, { useCallback, useEffect } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import Heading from '@atlaskit/heading';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import UFOSegment from '@atlaskit/react-ufo/segment';
import Suspense from '@atlaskit/react-ufo/suspense';
import { token } from '@atlaskit/tokens';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { ExperienceStart as ExperienceStartComponent } from '@atlassian/conversation-assistant-instrumentation/ui/ExperienceTracker';
import { ExperienceFailure } from '@atlassian/conversation-assistant-instrumentation/ui/ExperienceTracker';
import type { AssistanceServiceProduct } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	useUIStoreActions,
	useUIStoreSubscription,
} from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { useReportInsightsBannerResolved } from '@atlassian/rovo-growth-pulse/common/analytics/use-report-insights-banner-resolved';
import { isInsightsScheduledDay } from '@atlassian/rovo-growth-pulse/common/utils/insights-schedule';
import { useInsightsStatus } from '@atlassian/rovo-growth-pulse/services/insights-status';
import { InsightsBanner } from '@atlassian/rovo-growth-pulse/ui/insights-banner';
import { PinnedWorkSection } from '@atlassian/rovo-pins/PinnedWorkSection';
import type { PinnedItem } from '@atlassian/rovo-pins/types';
import { PROMPT_LIBRARY_SKILL_TAG } from '@atlassian/rovo-skills/constants';
import { buildSpaceStarters } from '@atlassian/rovo-spaces/common/starters';
import { useSpaceStoreActions } from '@atlassian/rovo-spaces/controllers/space-store';
import { type CreateSpaceResponse } from '@atlassian/rovo-spaces/controllers/space-store/types';
import { generateOptimisticSpaceSources } from '@atlassian/rovo-spaces/controllers/space-store/utils';
import { useListSpaces } from '@atlassian/rovo-spaces/services/useListSpaces';
import type { SpaceSource } from '@atlassian/rovo-spaces/ui/CreateSpaceModal/types';

import { useForYouNavigationActions } from '../../services/for-you-navigation-store';
import { EmptyState } from '../common/empty-state';
import { ErrorState } from '../common/error-state';
import spaceCardMessages from '../my-spaces/messages';
import { MySpacesSkeleton } from '../my-spaces/my-spaces-skeleton';
import { MySpacesList } from '../my-spaces/MySpacesList';
import { ThreadsListSkeleton } from '../threads-list/threads-list-skeleton';
import { ThreadsList } from '../threads-list/ThreadsList';

import { FOR_YOU_TAB_ID, type ForYouPanelProps } from './types';

const messages = defineMessages({
	insightsSectionHeading: {
		id: 'rovo-for-you.for-you-panel.insightsSectionHeading',
		defaultMessage: 'Insights',
		description: 'Heading for the insights section in the Your work tab',
	},
});

/** ErrorBoundary fallback for ThreadsList — renders the error state and marks the UFO experience as failed. */
const ThreadsListErrorFallback = ({ error }: { error: Error }) => (
	<>
		<ExperienceFailure name={ExperienceName.THREADS_LIST_QUERY} error={error} />
		<ErrorState />
	</>
);

const styles = cssMap({
	container: {
		width: '100%',
		flex: '1',
		minHeight: '0px',
		display: 'flex',
		flexDirection: 'column',
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
	},
	// Child content fills remaining space; scrolling is owned by the RovoTabs panel above
	contentWrapper: {
		flex: '1',
		minHeight: '0px',
		display: 'flex',
		flexDirection: 'column',
	},
	// Gives the empty state the same height as MySpacesSkeleton (3 cards) so the
	// modal doesn't collapse when both sections have no content.
	// 3 cards × ~108px each + 2 inter-card gaps of 16px = 356px
	emptyStateWrapper: {
		minHeight: '356px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
});

/**
 * Renders the "Your Rovo insights" entry-point banner at the top of the For You tab.
 * On click, navigates the Rovo side panel to the pulse menu so the user can read their full Rovo insights.
 * Mirrors the gating used by `RovoEmptyState`.
 */
const InsightsBannerSection = ({ cloudId, product }: { cloudId: string; product?: string }) => {
	const intl = useIntl();
	useUIStoreSubscription();
	const { setActiveMenu } = useUIStoreActions();

	const pulseEnabled = FeatureGates.getExperimentValue(
		'rovo_growth_chat_pulse',
		'isEnabled',
		false,
	);
	const scheduleOk = pulseEnabled
		? fg('rovo_growth_insights_banner_ignore_schedule') || isInsightsScheduledDay()
		: false;
	// Without a host product we can't attribute the backend call correctly, so
	// skip fetching insights status entirely in that case.
	const statusEnabled = !!product && pulseEnabled && scheduleOk;

	const { isInsightsAvailable, generatedAt, isFresh, statusFetchState } = useInsightsStatus({
		enabled: statusEnabled,
		assistanceServiceParams: {
			touchpointSource: 'rovo-for-you-tab',
			product: product as AssistanceServiceProduct,
			experienceId: 'ai-mate',
			siteId: cloudId,
		},
	});

	// Report how the insights entry point resolved (shown, or why not) for per-user diagnosis.
	useReportInsightsBannerResolved({
		surface: 'yourWork',
		pulseEnabled,
		requiresProduct: true,
		hasProduct: !!product,
		scheduleOk,
		statusFetchState,
		isInsightsAvailable,
		requiresFresh: false,
		isFresh,
		hasSelectedSpace: false,
		generatedAt,
	});

	const handleClick = useCallback(() => {
		setActiveMenu('pulse');
	}, [setActiveMenu]);

	if (!isInsightsAvailable) {
		return null;
	}

	return (
		<UFOSegment name={'for-you-insights-banner'}>
			<Stack space="space.200">
				<Heading size="xxsmall" as="h4">
					{intl.formatMessage(messages.insightsSectionHeading)}
				</Heading>
				<InsightsBanner onClick={handleClick} appearance="yourWork" generatedAt={generatedAt} />
			</Stack>
		</UFOSegment>
	);
};

const ForYouPanelContent = ({
	cloudId,
	userId,
	product,
	isActive,
	onChatEmptyLaunch,
}: ForYouPanelProps) => {
	const intl = useIntl();
	const { resetNavigation } = useForYouNavigationActions();
	const { setActiveMenu } = useUIStoreActions();
	const { sendAnalyticsEvent } = useAnalytics();

	const pinnedWorkEnabled = FeatureGates.getExperimentValue(
		'rovo_your_work_tab_pin_objects',
		'isEnabled',
		false,
	);

	const handlePinWorkClick = useCallback(() => {
		sendAnalyticsEvent({
			eventType: 'ui',
			actionSubject: 'pinWork',
			action: 'clicked',
		});
		setActiveMenu('search-work');
	}, [sendAnalyticsEvent, setActiveMenu]);

	// Reuses the same onChatEmptyLaunch path as the "Chat" button on a space card, scoped to a
	// non-space item via `selectedPinnedItem` — no space pill, no RAG scoping, no
	// space-landing navigation (see handleChatEmptyLaunch). No custom greeting either — the
	// item's title bar (once built) is the identity, and the default greeting + these
	// starters are the content.
	const handlePinStartChat = useCallback(
		(item: PinnedItem) => {
			sendAnalyticsEvent({
				eventType: 'ui',
				actionSubject: 'pinnedItem',
				action: 'clicked',
				actionSubjectId: 'startChat',
				attributes: { pinType: item.pinType },
			});
			onChatEmptyLaunch?.({
				spaceId: '',
				spaceName: '',
				spaceEmoji: '',
				greetingTitle: '',
				selectedPinnedItem: {
					entityType: item.pinType,
					ari: item.ari,
					name: item.name,
					url: item.url,
				},
				// Reuses the same canonical starter prompts a space launch gets — see
				// packages/ai-mate/rovo-spaces/src/common/starters.tsx.
				customConversationActions: buildSpaceStarters(intl, { spaceName: item.name }),
				replaceProviderActions: true,
				skillRegistryTags: [PROMPT_LIBRARY_SKILL_TAG],
			});
		},
		[onChatEmptyLaunch, intl, sendAnalyticsEvent],
	);

	// Spaces data is fetched here (rather than inside `MySpacesList`) so the same data — and
	// the optimistic-insert callback below — can be shared with `ThreadsList`. `ThreadsList`
	// needs the spaces' aggregated conversationIds to filter out threads that have already been
	// organized into a space, and needs a way to add the just-created space to the store so My
	// Spaces reflects it immediately without an extra `GET /spaces` round-trip.
	const {
		spaces,
		isLoading: isLoadingSpaces,
		error: spacesError,
		hasFetched: spacesFetched,
		conversationIds: spacesConversationIds,
	} = useListSpaces({
		cloudId,
		product: 'rovo',
		experienceId: 'ai-mate',
	});

	const { addSpace, patchSpace } = useSpaceStoreActions();

	// When a space is created from the create-space modal (launched from a thread card in
	// `ThreadsList`), do two things:
	//   1. Optimistically insert the new space into the SpaceStore so My Spaces reflects it
	//      immediately (existing behaviour).
	//   2. Navigate the user into the new space's empty-state Chat via the canonical
	//      `onChatEmptyLaunch` path — the same launch used by the "Chat" button on a space card.
	//      This tears down the For You panel and lands the user on the space landing page.
	// The optimistic insert runs first so the space-landing surface renders against a store that
	// already contains the new space. `launchTabId: FOR_YOU_TAB_ID` ensures the chat header's
	// back button returns the user to the For You tab.
	const handleSpaceCreated = useCallback(
		(
			space: CreateSpaceResponse,
			emojiId?: string,
			attachedConversationId?: string,
			sources?: SpaceSource[],
		) => {
			addSpace(space, emojiId);
			// Seed the just-organized thread's conversationId onto the optimistic space's sources so
			// the SpaceStore `conversationIds` rollup (which `ThreadsList` filters on) includes it
			// immediately — hiding the thread from suggestions without waiting for a `GET /spaces`
			// refetch, and surviving the For You panel remount that the launch below triggers. Mirrors
			// the side-nav/Recommended create flow (`addSpace` + `patchSpace`); the one divergence is
			// injecting `attachedConversationId` because `extractSourcesFromThread` zeroes source
			// conversationIds. When `attachedConversationId` is absent (the modal's conversation
			// attach failed), we skip the patch and fall back to the refetch.
			if (attachedConversationId && sources && sources.length > 0) {
				patchSpace(space.id, {
					sources: generateOptimisticSpaceSources(sources, [attachedConversationId]),
				});
			}
			onChatEmptyLaunch?.({
				spaceId: space.id,
				spaceName: space.name,
				spaceEmoji: emojiId ?? '',
				greetingTitle: intl.formatMessage(spaceCardMessages.chatGreeting),
				replaceProviderActions: true,
				skillRegistryTags: [PROMPT_LIBRARY_SKILL_TAG],
				launchTabId: FOR_YOU_TAB_ID,
				sources: sources?.map((s) => ({
					ari: s.ari,
					url: s.url,
					sourceType: s.sourceType,
					name: s.name,
				})),
			});
		},
		[addSpace, patchSpace, onChatEmptyLaunch, intl],
	);

	// When the tab becomes active, fire the screen analytics event.
	// When it becomes inactive, reset navigation so returning starts fresh at threads-list.
	useEffect(() => {
		if (isActive) {
			sendAnalyticsEvent({
				eventType: 'screen',
				name: 'forYouTab',
			});
		} else {
			resetNavigation();
		}
	}, [isActive, sendAnalyticsEvent, resetNavigation]);

	// Empty determination is derived directly from the store data (not from child callbacks).
	// This eliminates the previous cascade of `onEmpty` callbacks writing to parent state,
	// which caused an isEmpty flap `true → false → true` on every mount and 2-3 wasted
	// panel re-renders per settled state.
	//
	// Threads emptiness is derived from the same rule ThreadsList uses (edges filtered by
	// spacesConversationIds), but only *after* both queries have resolved — we cannot know
	// threads' emptiness pre-Suspense-resolve at this level without lifting the query. We
	// therefore keep an onEmpty callback for threads (the only remaining child callback)
	// and default `bothEmpty` to false until both signals are available.
	const isSpacesEmpty = spacesFetched && (!!spacesError || !spaces || spaces.length === 0);
	const [isThreadsEmpty, setIsThreadsEmpty] = React.useState(false);
	const handleThreadsEmpty = React.useCallback((isEmpty: boolean) => {
		setIsThreadsEmpty(isEmpty);
	}, []);
	const bothEmpty = isSpacesEmpty && isThreadsEmpty;

	// Track when the combined empty state is shown (both sections have no data).
	useEffect(() => {
		if (bothEmpty && isActive) {
			sendAnalyticsEvent({
				eventType: 'screen',
				name: 'forYouEmptyState',
			});
		}
	}, [bothEmpty, isActive, sendAnalyticsEvent]);

	if (!cloudId || !userId) {
		return (
			<Stack space="space.300">
				<MySpacesSkeleton />
				<ThreadsListSkeleton />
			</Stack>
		);
	}

	return (
		<Stack space="space.300">
			<ErrorBoundary FallbackComponent={ErrorState}>
				<InsightsBannerSection cloudId={cloudId} product={product} />
			</ErrorBoundary>
			{pinnedWorkEnabled && (
				// `usePinnedItems` (inside PinnedWorkSection) shares its cache with the Search
				// page by cloudId/product, not by a common provider ancestor — this only gates
				// whether the section renders at all.
				<ErrorBoundary FallbackComponent={ErrorState}>
					<PinnedWorkSection
						cloudId={cloudId}
						product={product}
						onPinWorkClick={handlePinWorkClick}
						onStartChat={handlePinStartChat}
					/>
				</ErrorBoundary>
			)}
			{bothEmpty ? (
				<Box xcss={styles.emptyStateWrapper}>
					<EmptyState />
				</Box>
			) : (
				<>
					<ErrorBoundary FallbackComponent={ErrorState}>
						<MySpacesList
							cloudId={cloudId}
							spaces={spaces}
							isLoading={isLoadingSpaces}
							hasFetched={spacesFetched}
							error={spacesError}
							onChatEmptyLaunch={onChatEmptyLaunch}
						/>
					</ErrorBoundary>
					<ErrorBoundary FallbackComponent={ThreadsListErrorFallback}>
						{/* ExperienceStart placed before Suspense so timing includes async bundle load */}
						<ExperienceStartComponent name={ExperienceName.THREADS_LIST_QUERY} />
						<Suspense fallback={<ThreadsListSkeleton />} interactionName="for-you-threads-list">
							<ThreadsList
								cloudId={cloudId}
								userId={userId}
								spacesConversationIds={spacesConversationIds}
								onSpaceCreated={handleSpaceCreated}
								onEmpty={handleThreadsEmpty}
								product={(product ?? 'rovo') as AssistanceServiceProduct}
							/>
						</Suspense>
					</ErrorBoundary>
				</>
			)}
		</Stack>
	);
};

/**
 * Root container for the "For You" tab.
 * Expects ForYouNavigationProvider to be mounted by an ancestor (e.g. main.tsx).
 */
export const ForYouPanel = (props: ForYouPanelProps): React.JSX.Element => {
	return (
		<UFOSegment name="for-you-panel">
			<Box xcss={styles.container} testId="for-you-panel">
				<Box xcss={styles.contentWrapper}>
					<ForYouPanelContent {...props} />
				</Box>
			</Box>
		</UFOSegment>
	);
};
